import { Helmet } from "react-helmet-async";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import ImagePreviewDialog from "@/components/ImagePreviewDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import {
  Search,
  Star,
  TrendingUp,
  MessageSquare,
  MessageCircle,
  Shield,
  BarChart3,
  Globe,
  Users,
  FileText,
  Phone,
  Mail,
  ChevronDown,
  Target,
  Award,
  Zap,
  MapPin,
  Calendar,
  Quote,
  ThumbsUp,
  AlertTriangle,
  User
} from "lucide-react";
import { useState, useEffect } from "react";
import { brandLogos } from "@/constants/brandLogos";
import { LANDING_COLORS } from "@/lib/landing-styles";
import { RevealOnScroll, FloatingOrbs } from "@/components/landing";

const Individuals = () => {
  const [selectedTab, setSelectedTab] = useState<"short" | "detailed">("short");
  const [api, setApi] = useState<any>(null);

  // Auto-scroll effect for carousel
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 2000);
    return () => clearInterval(interval);
  }, [api]);


  return (
    <div className="min-h-screen font-['Inter']">
      <Helmet>
        <title>Google My Business Setup & Local SEO Services for Individuals | GMB Optimization</title>
        <meta name="description" content="Turn Google searches into new clients with our Google My Business setup and local SEO services. Professional GMB optimization, profile management, and local ranking improvement for individual businesses." />
        <meta name="keywords" content="Google My Business setup, local SEO services, Google My Business optimization, GMB profile management, local search optimization, Google Business Profile setup, local SEO consultation, Google My Business verification" />
        <link rel="canonical" href="https://fly-social.com/individuals" />

        {/* Open Graph tags */}
        <meta property="og:title" content="Google My Business Setup & Local SEO Services for Individuals | GMB Optimization" />
        <meta property="og:description" content="Turn Google searches into new clients with our Google My Business setup and local SEO services. Professional GMB optimization, profile management, and local ranking improvement." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fly-social.com/individuals" />
        <meta property="og:image" content="https://fly-social.com/lovable-uploads/26188721-2020-4a7f-bc12-f030b28db398.png" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Google My Business Setup & Local SEO Services for Individuals | GMB Optimization" />
        <meta name="twitter:description" content="Turn Google searches into new clients with our Google My Business setup and local SEO services. Professional GMB optimization, profile management, and local ranking improvement." />
        <meta name="twitter:image" content="https://fly-social.com/lovable-uploads/26188721-2020-4a7f-bc12-f030b28db398.png" />

        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Google My Business Setup & Local SEO for Individuals",
            "description": "Professional Google My Business setup and local SEO services for individual business owners",
            "provider": {
              "@type": "Organization",
              "name": "Fly - ZoomLocal Tech",
              "url": "https://fly-social.com"
            },
            "serviceType": "Local SEO Services",
            "areaServed": "Worldwide",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Individual GMB Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Google My Business Setup"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Local SEO Optimization"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Google Business Profile Management"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <NavBar />
      
      {/* Hero Section */}
      <section
        className="relative pt-24 md:pt-32 pb-12 px-4 md:px-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0fdf4 40%, #ecfeff 100%)' }}
      >
        <FloatingOrbs variant="hero" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Content Left */}
            <div className="lg:w-1/2 text-center lg:text-left">
              <RevealOnScroll>
                {/* Badge */}
                <span
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 mb-6 rounded-full text-sm font-semibold border"
                  style={{
                    background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
                    borderColor: `${LANDING_COLORS.primary}30`,
                    color: LANDING_COLORS.primary
                  }}
                >
                  <User className="h-4 w-4" />
                  FOR INDIVIDUAL BUSINESS OWNERS
                </span>

                {/* Main Headline */}
                <h1
                  className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6"
                  style={{ color: LANDING_COLORS.text.primary }}
                >
                  Turn Google Searches into{" "}
                  <span style={{ color: LANDING_COLORS.primary }}>New Clients</span> & Higher Income
                </h1>

                {/* Subtext */}
                <p
                  className="text-lg md:text-xl mb-8 max-w-2xl leading-relaxed"
                  style={{ color: LANDING_COLORS.text.secondary }}
                >
                  Your expertise deserves visibility. With Fly's Google My Business (GMB) optimization, more people in your city discover, trust, and{" "}
                  <span style={{ color: LANDING_COLORS.google.yellow }} className="font-semibold">book you</span> directly from Google Business Profile (GBP) searches.
                </p>

                {/* WhatsApp CTA */}
                <Button
                  size="lg"
                  className="text-white font-bold px-8 py-4 text-lg transition-all duration-300 hover:scale-105 rounded-2xl"
                  style={{
                    background: `linear-gradient(135deg, ${LANDING_COLORS.whatsapp.green} 0%, ${LANDING_COLORS.whatsapp.dark} 100%)`,
                  }}
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.open('https://wa.me/918446712833', '_blank');
                    }
                  }}
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Chat on WhatsApp
                </Button>
              </RevealOnScroll>
            </div>
            
            {/* Image Right */}
            <div className="lg:w-1/2 relative">
              <RevealOnScroll delay={1} direction="right">
              <div className="relative group">
                {/* Background effects */}
                <div
                  className="absolute -inset-4 rounded-3xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity duration-500"
                  style={{ background: `linear-gradient(135deg, ${LANDING_COLORS.primary}20 0%, ${LANDING_COLORS.accent}20 100%)` }}
                />

                {/* Main image container */}
                <div className="relative transform group-hover:scale-105 transition-all duration-500">
                  <img
                    src="/lovable-uploads/26188721-2020-4a7f-bc12-f030b28db398.png"
                    alt="With Fly Retail - See the business Growth In 60 days"
                    className="w-full h-auto rounded-3xl shadow-2xl"
                  />
                </div>
                
                {/* Additional glow effect */}
                <div
                  className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `linear-gradient(135deg, ${LANDING_COLORS.primary}10 0%, ${LANDING_COLORS.accent}10 100%)` }}
                />
              </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* Growth Statistics */}
      <section className="py-16 px-4 md:px-10 bg-background">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            How Local Business Grow with Fly
          </h2>
          <p className="text-lg text-muted-foreground mb-12">
            More visibility means more clients, more trust, and more income.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-center">
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:bg-gradient-to-br hover:from-yellow-200 hover:to-yellow-300 transition-all duration-500 ease-out cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full bg-gray-100 text-gray-600 group-hover:bg-yellow-400 group-hover:text-yellow-800 group-hover:scale-110 transition-all duration-300">
                    <Phone className="w-8 h-8" />
                  </div>
                </div>
                <div className="text-2xl font-bold mb-3 group-hover:scale-105 transition-transform duration-300" style={{ color: '#7B61FF' }}>
                  20–40%
                </div>
                <p className="text-lg font-semibold text-gray-900 group-hover:scale-105 transition-transform duration-300">
                  More Calls within 60 days
                </p>
              </CardContent>
            </Card>
            
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:bg-gradient-to-br hover:from-green-300 hover:to-green-400 transition-all duration-500 ease-out cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full bg-gray-100 text-gray-600 group-hover:bg-green-500 group-hover:text-green-900 group-hover:scale-110 transition-all duration-300">
                    <TrendingUp className="w-8 h-8" />
                  </div>
                </div>
                <div className="text-2xl font-bold mb-3 group-hover:scale-105 transition-transform duration-300" style={{ color: '#7B61FF' }}>
                  15–30%
                </div>
                <p className="text-lg font-semibold text-gray-900 group-hover:scale-105 transition-transform duration-300">
                  More Inquiries/Bookings
                </p>
              </CardContent>
            </Card>
            
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:bg-gradient-to-br hover:from-blue-300 hover:to-blue-400 transition-all duration-500 ease-out cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full bg-gray-100 text-gray-600 group-hover:bg-blue-500 group-hover:text-blue-900 group-hover:scale-110 transition-all duration-300">
                    <Star className="w-8 h-8" />
                  </div>
                </div>
                <div className="text-2xl font-bold mb-3 group-hover:scale-105 transition-transform duration-300" style={{ color: '#7B61FF' }}>
                  Steady Growth
                </div>
                <p className="text-lg font-semibold text-gray-900 group-hover:scale-105 transition-transform duration-300">
                  in Sales
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Authorized Google API Partner Section */}
      <section className="py-16 px-4 md:px-10 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-white">
            {/* Google API Partner */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Authorized Google API Partner</h3>
              <p className="text-white/80 text-lg">Secure Integration</p>
            </div>

            {/* Used by 1000+ Locations */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Used by 1000+ Locations</h3>
              <p className="text-white/80 text-lg">Trusted by businesses worldwide</p>
            </div>

            {/* 24/7 Support */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">24/7 Support</h3>
              <p className="text-white/80 text-lg">Always here when you need us</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-purple-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-32 h-32 bg-purple-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-yellow-200 rounded-full blur-2xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block bg-white/80 backdrop-blur-sm rounded-full px-6 py-2 mb-4 shadow-sm">
              <p className="text-sm font-medium text-primary uppercase tracking-wider">Trusted by</p>
            </div>
            <h3 className="text-3xl font-bold text-foreground mb-2">Top consumer brands across the globe</h3>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20">
              <Carousel 
                opts={{
                  align: "start",
                  loop: true,
                  dragFree: true
                }} 
                setApi={setApi} 
                className="relative px-10"
              >
                <CarouselContent className="-ml-2 md:-ml-4">
                  {brandLogos.map((brand, index) => (
                    <CarouselItem key={index} className="pl-2 md:pl-4 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/4 xl:basis-1/5 flex items-center justify-center">
                      <div className="h-36 sm:h-40 md:h-44 w-full flex items-center justify-center p-4 rounded-xl bg-white/80 hover:bg-white hover:shadow-md transition-all duration-300 transform hover:scale-105">
                        <img
                          src={brand.logo}
                          alt={`${brand.name} logo`}
                          className="w-full h-full object-contain"
                          style={{ maxWidth: '200px', maxHeight: '140px' }}
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-0 bg-white/90 backdrop-blur-sm hover:bg-white shadow-lg border-0" />
                <CarouselNext className="right-0 bg-white/90 backdrop-blur-sm hover:bg-white shadow-lg border-0" />
              </Carousel>
            </div>
          </div>
        </div>
      </section>

      {/* Why Visibility Matters */}
      <section className="py-16 px-4 md:px-10 bg-gradient-to-b from-cyan-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why <span style={{ color: '#7B61FF' }}>Visibility</span> on Google Matters for Local Business
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your expertise means nothing if clients can't find you. Here's why Google visibility directly impacts your business growth and income.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full bg-yellow-100 text-gray-600 group-hover:bg-yellow-200 transition-colors duration-300">
                    <MapPin className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Clients Compare on Google
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Most people search 'specialist near me' before booking. Being visible puts you in the first choice set.
                </p>
              </CardContent>
            </Card>
            
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full bg-yellow-100 text-gray-600 group-hover:bg-yellow-200 transition-colors duration-300">
                    <Star className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Reviews Build Trust
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Genuine reviews and professional replies influence decisions and build confidence.
                </p>
              </CardContent>
            </Card>
            
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full bg-yellow-100 text-gray-600 group-hover:bg-yellow-200 transition-colors duration-300">
                    <Calendar className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Consistent Updates Win
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Active profiles earn more clicks, directions, and visits.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How Fly Transforms Your Google Presence */}
      <section className="py-16 px-4 md:px-10 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Fly Retail Transforms Your Google My Business Presence
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Nine proven steps that turn your Google Business Profile (GBP) and GMB listings into powerful customer acquisition engines.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Step 01 */}
            <Card className="group relative bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-purple-50 hover:to-purple-100 transition-all duration-300 cursor-pointer overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-start mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#7B61FF' }}>
                    01
                  </div>
                </div>
                 <ImagePreviewDialog 
                   src="/lovable-uploads/cf60430a-6a90-4dd7-b0f3-5f6da8cecf82.png" 
                   alt="SEO Dashboard Analytics Interface"
                 >
                   <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 group-hover:border-purple-300 transition-all duration-300 overflow-hidden shadow-sm p-2 cursor-pointer hover:shadow-lg">
                     <img 
                       src="/lovable-uploads/cf60430a-6a90-4dd7-b0f3-5f6da8cecf82.png" 
                       alt="SEO Dashboard Analytics Interface" 
                       className="w-full h-full object-contain rounded-lg hover:scale-105 transition-transform"
                     />
                   </div>
                 </ImagePreviewDialog>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  SEO Audit
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Uncover hidden opportunities and fix critical issues blocking your Google visibility.
                </p>
              </CardContent>
            </Card>

            {/* Step 02 */}
            <Card className="group relative bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100 transition-all duration-300 cursor-pointer overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-start mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#7B61FF' }}>
                    02
                  </div>
                </div>
                 <ImagePreviewDialog 
                   src="/lovable-uploads/ac580519-a727-4632-82e6-472909bebf92.png" 
                   alt="Profile Management Tools"
                 >
                   <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 group-hover:border-blue-300 transition-all duration-300 overflow-hidden shadow-sm p-2 cursor-pointer hover:shadow-lg">
                     <img 
                       src="/lovable-uploads/ac580519-a727-4632-82e6-472909bebf92.png" 
                       alt="Profile Management Tools" 
                       className="w-full h-full object-contain rounded-lg hover:scale-105 transition-transform"
                     />
                   </div>
                 </ImagePreviewDialog>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Profile Optimisation
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Transform your profiles into customer magnets that drive more calls and visits.
                </p>
              </CardContent>
            </Card>

            {/* Step 03 */}
            <Card className="group relative bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-green-50 hover:to-green-100 transition-all duration-300 cursor-pointer overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-start mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#7B61FF' }}>
                    03
                  </div>
                </div>
                 <ImagePreviewDialog 
                   src="/lovable-uploads/bf1024e6-bdde-4c8e-a36b-7b94b08e6c0b.png" 
                   alt="Reviews Dashboard"
                 >
                   <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 group-hover:border-green-300 transition-all duration-300 overflow-hidden shadow-sm p-2 cursor-pointer hover:shadow-lg">
                     <img 
                       src="/lovable-uploads/bf1024e6-bdde-4c8e-a36b-7b94b08e6c0b.png" 
                       alt="Reviews Dashboard" 
                       className="w-full h-full object-contain rounded-lg hover:scale-105 transition-transform"
                     />
                   </div>
                 </ImagePreviewDialog>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Review Response & Insights
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Turn every review into a growth opportunity with AI-powered responses and insights.
                </p>
              </CardContent>
            </Card>

            {/* Step 04 */}
            <Card className="group relative bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-yellow-50 hover:to-yellow-100 transition-all duration-300 cursor-pointer overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-start mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#7B61FF' }}>
                    04
                  </div>
                </div>
                 <ImagePreviewDialog 
                   src="/lovable-uploads/9076b9e2-18cf-4c3e-b58d-857eef25109a.png" 
                   alt="Product Catalog Interface"
                 >
                   <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 group-hover:border-yellow-300 transition-all duration-300 overflow-hidden shadow-sm p-2 cursor-pointer hover:shadow-lg">
                     <img 
                       src="/lovable-uploads/9076b9e2-18cf-4c3e-b58d-857eef25109a.png" 
                       alt="Product Catalog Interface" 
                       className="w-full h-full object-contain rounded-lg hover:scale-105 transition-transform"
                     />
                   </div>
                 </ImagePreviewDialog>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Products Addition
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Easily add and manage products so customers can browse and buy directly from your profile.
                </p>
              </CardContent>
            </Card>

            {/* Step 05 */}
            <Card className="group relative bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-red-50 hover:to-red-100 transition-all duration-300 cursor-pointer overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-start mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#7B61FF' }}>
                    05
                  </div>
                </div>
                 <ImagePreviewDialog 
                   src="/lovable-uploads/6c2d6c61-67a8-4405-8c66-6651a6048c88.png" 
                   alt="Content Management System"
                 >
                   <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 group-hover:border-red-300 transition-all duration-300 overflow-hidden shadow-sm p-2 cursor-pointer hover:shadow-lg">
                     <img 
                       src="/lovable-uploads/6c2d6c61-67a8-4405-8c66-6651a6048c88.png" 
                       alt="Content Management System" 
                       className="w-full h-full object-contain rounded-lg hover:scale-105 transition-transform"
                     />
                   </div>
                 </ImagePreviewDialog>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Post & Updates
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Share timely updates, promotions, and events to keep customers engaged and coming back.
                </p>
              </CardContent>
            </Card>

            {/* Step 06 */}
            <Card className="group relative bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-indigo-50 hover:to-indigo-100 transition-all duration-300 cursor-pointer overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-start mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#7B61FF' }}>
                    06
                  </div>
                </div>
                 <ImagePreviewDialog 
                   src="/lovable-uploads/b6db2dda-4244-460f-bcad-0214fb1bda36.png" 
                   alt="Landing Page Builder"
                 >
                   <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 group-hover:border-indigo-300 transition-all duration-300 overflow-hidden shadow-sm p-2 cursor-pointer hover:shadow-lg">
                     <img 
                       src="/lovable-uploads/b6db2dda-4244-460f-bcad-0214fb1bda36.png" 
                       alt="Landing Page Builder" 
                       className="w-full h-full object-contain rounded-lg hover:scale-105 transition-transform"
                     />
                   </div>
                 </ImagePreviewDialog>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Landing Page / Microsite
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Create a professional Google-linked microsite to showcase your services and attract more customers.
                </p>
              </CardContent>
            </Card>

            {/* Step 07 */}
            <Card className="group relative bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-pink-50 hover:to-pink-100 transition-all duration-300 cursor-pointer overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-start mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#7B61FF' }}>
                    07
                  </div>
                </div>
                 <ImagePreviewDialog 
                   src="/lovable-uploads/2de5924f-a971-49e7-a552-95fd1b2d4ed8.png" 
                   alt="Performance Analytics Dashboard"
                 >
                   <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 group-hover:border-pink-300 transition-all duration-300 overflow-hidden shadow-sm p-2 cursor-pointer hover:shadow-lg">
                     <img 
                       src="/lovable-uploads/2de5924f-a971-49e7-a552-95fd1b2d4ed8.png" 
                       alt="Performance Analytics Dashboard" 
                       className="w-full h-full object-contain rounded-lg hover:scale-105 transition-transform"
                     />
                   </div>
                 </ImagePreviewDialog>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Track Profile Performance
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  See exactly how your optimisations drive more customers through your doors.
                </p>
              </CardContent>
            </Card>

            {/* Step 08 */}
            <Card className="group relative bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-teal-50 hover:to-teal-100 transition-all duration-300 cursor-pointer overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-start mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#7B61FF' }}>
                    08
                  </div>
                </div>
                 <ImagePreviewDialog 
                   src="/lovable-uploads/70d1b73e-f333-446f-95ff-2e6bbe8d6894.png" 
                   alt="Competitor Analysis Dashboard"
                 >
                   <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 group-hover:border-teal-300 transition-all duration-300 overflow-hidden shadow-sm p-2 cursor-pointer hover:shadow-lg">
                     <img 
                       src="/lovable-uploads/70d1b73e-f333-446f-95ff-2e6bbe8d6894.png" 
                       alt="Competitor Analysis Dashboard" 
                       className="w-full h-full object-contain rounded-lg hover:scale-105 transition-transform"
                     />
                   </div>
                 </ImagePreviewDialog>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Competitor Analysis
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Stay ahead by monitoring how your business stacks up against competitors in your area.
                </p>
              </CardContent>
            </Card>

            {/* Step 09 */}
            <Card className="group relative bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-orange-50 hover:to-orange-100 transition-all duration-300 cursor-pointer overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-start mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#7B61FF' }}>
                    09
                  </div>
                </div>
                 <ImagePreviewDialog 
                   src="/lovable-uploads/5e00cf30-8fc3-401c-9674-27ab4711872e.png" 
                   alt="Expert Support Consultation"
                 >
                   <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 group-hover:border-orange-300 transition-all duration-300 overflow-hidden shadow-sm p-2 cursor-pointer hover:shadow-lg">
                     <img 
                       src="/lovable-uploads/5e00cf30-8fc3-401c-9674-27ab4711872e.png" 
                       alt="Expert Support Consultation" 
                       className="w-full h-full object-contain rounded-lg hover:scale-105 transition-transform"
                     />
                   </div>
                 </ImagePreviewDialog>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Full Profile Support (Google Expert)
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Never worry about Google issues again with dedicated expert support.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <Button
              size="lg"
              className="px-12 py-6 text-xl text-white font-bold rounded-xl hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg"
              style={{ backgroundColor: '#7B61FF' }}
              onClick={() => {
                if (typeof document !== 'undefined') {
                  document.querySelector('[data-section="growth-plan"]')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }}
            >
              Get Your Google Profile Checked Today
            </Button>
          </div>
        </div>
      </section>



      {/* What Clients Say */}
      <section className="py-16 px-4 md:px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What <span style={{ color: '#7B61FF' }}>Clients</span> Say
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:border-2 transition-all duration-300 cursor-pointer" style={{ borderColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = '#7B61FF'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}>
              <CardContent className="p-6 relative">
                <div className="absolute top-4 left-4">
                  <Quote className="w-6 h-6 group-hover:text-purple-600 group-hover:scale-110 transition-all duration-300" style={{ color: '#7B61FF' }} />
                </div>
                <div className="mt-8 mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Riya S.</h3>
                  <p className="text-sm text-gray-500 mb-4">Restaurant Owner</p>
                </div>
                <blockquote className="text-gray-700 text-sm leading-relaxed mb-4">
                  "We started seeing more calls within 3 weeks. The weekly posts and review replies make a huge difference."
                </blockquote>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 */}
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:border-2 transition-all duration-300 cursor-pointer" style={{ borderColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = '#7B61FF'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}>
              <CardContent className="p-6 relative">
                <div className="absolute top-4 left-4">
                  <Quote className="w-6 h-6 group-hover:text-purple-600 group-hover:scale-110 transition-all duration-300" style={{ color: '#7B61FF' }} />
                </div>
                <div className="mt-8 mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Aditya M.</h3>
                  <p className="text-sm text-gray-500 mb-4">Salon Owner</p>
                </div>
                <blockquote className="text-gray-700 text-sm leading-relaxed mb-4">
                  "Profile optimization fixed our category mess; ranks better for 'salon near me' now."
                </blockquote>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 */}
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:border-2 transition-all duration-300 cursor-pointer" style={{ borderColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = '#7B61FF'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}>
              <CardContent className="p-6 relative">
                <div className="absolute top-4 left-4">
                  <Quote className="w-6 h-6 group-hover:text-purple-600 group-hover:scale-110 transition-all duration-300" style={{ color: '#7B61FF' }} />
                </div>
                <div className="mt-8 mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Anjali P.</h3>
                  <p className="text-sm text-gray-500 mb-4">Retail Store Owner</p>
                </div>
                <blockquote className="text-gray-700 text-sm leading-relaxed mb-4">
                  "They reply to every review professionally. Our rating moved from 4.1 → 4.6."
                </blockquote>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 4 */}
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:border-2 transition-all duration-300 cursor-pointer" style={{ borderColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = '#7B61FF'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}>
              <CardContent className="p-6 relative">
                <div className="absolute top-4 left-4">
                  <Quote className="w-6 h-6 group-hover:text-purple-600 group-hover:scale-110 transition-all duration-300" style={{ color: '#7B61FF' }} />
                </div>
                <div className="mt-8 mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Dr. Sharma</h3>
                  <p className="text-sm text-gray-500 mb-4">Medical Practice</p>
                </div>
                <blockquote className="text-gray-700 text-sm leading-relaxed mb-4">
                  "Patient inquiries increased by 35% in two months. The local keyword insights were game-changing."
                </blockquote>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 5 */}
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:border-2 transition-all duration-300 cursor-pointer" style={{ borderColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = '#7B61FF'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}>
              <CardContent className="p-6 relative">
                <div className="absolute top-4 left-4">
                  <Quote className="w-6 h-6 group-hover:text-purple-600 group-hover:scale-110 transition-all duration-300" style={{ color: '#7B61FF' }} />
                </div>
                <div className="mt-8 mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Priya K.</h3>
                  <p className="text-sm text-gray-500 mb-4">Fitness Center Owner</p>
                </div>
                <blockquote className="text-gray-700 text-sm leading-relaxed mb-4">
                  "Great service and support. Weekly posts keep our profile active and engaging."
                </blockquote>
                <div className="flex gap-1">
                  {[...Array(4)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                  <Star className="w-4 h-4 text-yellow-400" />
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 6 */}
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:border-2 transition-all duration-300 cursor-pointer" style={{ borderColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = '#7B61FF'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}>
              <CardContent className="p-6 relative">
                <div className="absolute top-4 left-4">
                  <Quote className="w-6 h-6 group-hover:text-purple-600 group-hover:scale-110 transition-all duration-300" style={{ color: '#7B61FF' }} />
                </div>
                <div className="mt-8 mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Rajesh T.</h3>
                  <p className="text-sm text-gray-500 mb-4">Service Business Owner</p>
                </div>
                <blockquote className="text-gray-700 text-sm leading-relaxed mb-4">
                  "Professional review management helped us recover from negative feedback quickly."
                </blockquote>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How We Reply to Reviews */}
      <section className="py-16 px-4 md:px-10 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How We <span style={{ color: '#7B61FF' }}>Reply</span> to Reviews
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Professional, empathetic responses that turn feedback into opportunities and build trust with potential clients.
            </p>
          </div>
          
          {/* Toggle Switch */}
          <div className="flex justify-center mb-12">
            <div className="relative bg-gray-200 rounded-full p-1 flex">
              <div 
                className="absolute top-1 bottom-1 bg-white rounded-full shadow-md transition-all duration-300 ease-in-out"
                style={{
                  left: selectedTab === "short" ? "4px" : "50%",
                  right: selectedTab === "short" ? "50%" : "4px"
                }}
              />
              <button
                onClick={() => setSelectedTab("short")}
                className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${
                  selectedTab === "short" 
                    ? "text-gray-900" 
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Short
              </button>
              <button
                onClick={() => setSelectedTab("detailed")}
                className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${
                  selectedTab === "detailed" 
                    ? "text-gray-900" 
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Detailed
              </button>
            </div>
          </div>
          
          {/* Review Response Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Positive Review Card */}
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:border-2 transition-all duration-300 cursor-pointer" style={{ borderColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = '#10B981'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}>
              <CardContent className="p-6">
                {/* Icon and Title */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <ThumbsUp className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Positive</h3>
                </div>
                
                {/* Customer Feedback */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Customer says:</p>
                  <p className="text-gray-600 italic">"Loved the quick service and friendly staff."</p>
                </div>
                
                {/* Our Response */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Our Response ({selectedTab === "short" ? "Short" : "Detailed"}):</p>
                  <div className="bg-gray-100 rounded-lg p-4 border-l-4 border-green-500">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {selectedTab === "short" 
                        ? "Thank you, [Name]! We're glad the team made your visit easy. See you again soon."
                        : "Thank you so much, [Name]! We're thrilled to hear that our team provided quick and friendly service. Your positive feedback means the world to us and motivates our team. We can't wait to welcome you back and continue exceeding expectations. Have a wonderful day!"
                      }
                    </p>
                  </div>
                </div>
                
                {/* Outcome */}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <p className="text-sm font-medium text-gray-700">Encouraged repeat visit</p>
                </div>
              </CardContent>
            </Card>

            {/* Neutral Review Card */}
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:border-2 transition-all duration-300 cursor-pointer" style={{ borderColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = '#F59E0B'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}>
              <CardContent className="p-6">
                {/* Icon and Title */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Neutral</h3>
                </div>
                
                {/* Customer Feedback */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Customer says:</p>
                  <p className="text-gray-600 italic">"Service was fine, wait time was a bit long."</p>
                </div>
                
                {/* Our Response */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Our Response ({selectedTab === "short" ? "Short" : "Detailed"}):</p>
                  <div className="bg-gray-100 rounded-lg p-4 border-l-4 border-yellow-500">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {selectedTab === "short" 
                        ? "Thanks for the feedback, [Name]. We're working on reducing wait times. We'll prioritize your next visit!"
                        : "Thanks for the honest feedback, [Name]. We're adjusting peak-hour staffing this week to reduce wait times. If you share your preferred slot, we'll prioritize your next visit."
                      }
                    </p>
                  </div>
                </div>
                
                {/* Outcome */}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <p className="text-sm font-medium text-gray-700">Expectation reset</p>
                </div>
              </CardContent>
            </Card>

            {/* Negative Review Card */}
            <Card className="group bg-white border-0 shadow-md rounded-xl hover:shadow-xl hover:-translate-y-1 hover:border-2 transition-all duration-300 cursor-pointer" style={{ borderColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = '#EF4444'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}>
              <CardContent className="p-6">
                {/* Icon and Title */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Negative</h3>
                </div>
                
                {/* Customer Feedback */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Customer says:</p>
                  <p className="text-gray-600 italic">"Didn't get the service I booked."</p>
                </div>
                
                {/* Our Response */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Our Response ({selectedTab === "short" ? "Short" : "Detailed"}):</p>
                  <div className="bg-gray-100 rounded-lg p-4 border-l-4 border-red-500">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {selectedTab === "short" 
                        ? "We're sorry, [Name]. Let's make this right with a rebook or refund. Please DM your booking ID."
                        : "We're sorry about this experience, [Name]. We've flagged this internally and can make it right with a no-charge rebook or refund—whichever you prefer. Please DM your booking ID so we can resolve it today."
                      }
                    </p>
                  </div>
                </div>
                
                {/* Outcome */}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <p className="text-sm font-medium text-gray-700">Issue resolved / Rebooked</p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Footnote */}
          <div className="text-center mt-12">
            <p className="text-sm text-gray-500 max-w-2xl mx-auto">
              Sample responses for illustration. All responses are customized to your brand voice and specific situations.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 md:px-10 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Your Questions, Answered
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Everything you need to know about growing your business with Fly.
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="results" className="border-0">
              <Card className="group border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-lg transition-all duration-300">
                <AccordionTrigger className="px-6 py-6 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  <h3 className="text-lg font-semibold text-gray-900 text-left group-hover:text-purple-700 transition-colors duration-300">
                    How soon can I see results?
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    Most specialists see measurable improvements in 45-60 days as your optimized profile gains traction and attracts more client attention.
                  </p>
                </AccordionContent>
              </Card>
            </AccordionItem>

            <AccordionItem value="ownership" className="border-0">
              <Card className="group border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-lg transition-all duration-300">
                <AccordionTrigger className="px-6 py-6 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  <h3 className="text-lg font-semibold text-gray-900 text-left group-hover:text-purple-700 transition-colors duration-300">
                    Do I still own my Google profile?
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    Yes, you always own your profile. We simply optimize and manage it on your behalf while you retain complete ownership and control.
                  </p>
                </AccordionContent>
              </Card>
            </AccordionItem>

            <AccordionItem value="reviews" className="border-0">
              <Card className="group border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-lg transition-all duration-300">
                <AccordionTrigger className="px-6 py-6 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  <h3 className="text-lg font-semibold text-gray-900 text-left group-hover:text-purple-700 transition-colors duration-300">
                    Can you help with reviews?
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    Yes, we help request reviews and respond professionally. Our approach helps you build a stellar online reputation that attracts more clients.
                  </p>
                </AccordionContent>
              </Card>
            </AccordionItem>

            <AccordionItem value="social-media" className="border-0">
              <Card className="group border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-lg transition-all duration-300">
                <AccordionTrigger className="px-6 py-6 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  <h3 className="text-lg font-semibold text-gray-900 text-left group-hover:text-purple-700 transition-colors duration-300">
                    What if I already post on social media?
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    Fly complements your social presence by making Google work harder for you. Google Business Profile posting targets people actively searching for your services.
                  </p>
                </AccordionContent>
              </Card>
            </AccordionItem>

            <AccordionItem value="technical" className="border-0">
              <Card className="group border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-lg transition-all duration-300">
                <AccordionTrigger className="px-6 py-6 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  <h3 className="text-lg font-semibold text-gray-900 text-left group-hover:text-purple-700 transition-colors duration-300">
                    Do I need technical knowledge?
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    No. Everything is managed for you; you review and approve. We handle all the technical aspects while you focus on your business.
                  </p>
                </AccordionContent>
              </Card>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Ready to Attract More Clients Section */}
      <section className="py-16 px-4 md:px-10" style={{ background: `linear-gradient(135deg, ${LANDING_COLORS.whatsapp.green} 0%, ${LANDING_COLORS.whatsapp.dark} 100%)` }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Attract More Clients from Google?
          </h2>
          <p className="text-white/90 text-lg mb-6">
            Try Fly WhatsApp Agent free for 14 days. No credit card required.
          </p>
          <Button
            size="lg"
            className="bg-white hover:bg-gray-100 px-8 py-4 text-lg font-bold rounded-2xl transition-all duration-300 hover:scale-105"
            style={{ color: LANDING_COLORS.whatsapp.dark }}
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.href = '/agent/lite';
              }
            }}
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Start 14-Day Free Trial
          </Button>
        </div>
      </section>

      {/* Get Your Free Growth Plan Section */}
      <section className="py-16 px-4 md:px-10 bg-white" data-section="growth-plan">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Get Your Free Growth Plan</h3>
          <p className="text-lg text-gray-600 mb-10">
            Tell us about your practice and we'll create a custom strategy to attract more patients from Google.
          </p>
          
          <form className="space-y-6 max-w-xl mx-auto">
            <Input 
              placeholder="Name*" 
              className="bg-white border-gray-300 text-gray-900 h-12 text-lg"
            />
            <Input 
              placeholder="Number*" 
              className="bg-white border-gray-300 text-gray-900 h-12 text-lg"
            />
            <Input 
              placeholder="Email*" 
              type="email"
              className="bg-white border-gray-300 text-gray-900 h-12 text-lg"
            />
            <Button
              type="button"
              className="w-full bg-fly-purple hover:bg-fly-purple/90 text-white font-bold py-4 text-lg"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open('https://wa.me/918446712833', '_blank');
                }
              }}
            >
              Send My Growth Plan
            </Button>
          </form>
          
          <p className="text-sm text-gray-500 mt-6">
            We'll analyze your current Google presence and send you a detailed growth plan within 24 hours.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Individuals;